const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

// Only active Engagement Members can be suggested automatically.
export const isEligibleEngagementMember = (user) =>
  Number(user.status ?? 1) === 1 &&
  !Number(user.isBlocked || 0) &&
  (Number(user.roleId) === 4 ||
    normalize(user.role?.name || user.roleName) === "engagement member");

const isEligibleInspectionMember = (user) =>
  Number(user.status ?? 1) === 1 &&
  !Number(user.isBlocked || 0) &&
  ["inspection team member", "inspection member", "inspection team"].includes(
    normalize(user.role?.name || user.roleName || user.userRole?.name)
  );

// Match exact metadata only; ambiguous names and missing members require manual assignment.
export function findEngagementPartner(link, users) {
  const engagement = link.engagement || link;
  const members = users.filter(
    (user) =>
      isEligibleEngagementMember(user) &&
      (!engagement.firmId || Number(user.firmId) === Number(engagement.firmId))
  );
  const partnerId =
    engagement.engagementPartnerUserId ||
    engagement.partnerUserId ||
    engagement.assignedTo;
  const partner = normalize(engagement.engagementPartner);
  const email = normalize(
    engagement.engagementPartnerEmail || engagement.partnerEmail || partner
  );

  if (partnerId) {
    return (
      members.find((user) => Number(user.id) === Number(partnerId)) || null
    );
  }

  // With no engagement identity metadata, a sole eligible firm member is unambiguous.
  if (
    !partner &&
    !normalize(engagement.engagementPartnerEmail || engagement.partnerEmail)
  ) {
    return members.length === 1 ? members[0] : null;
  }

  const emailMatches = members.filter(
    (user) => email && normalize(user.emailId || user.email) === email
  );
  if (emailMatches.length) {
    return emailMatches.length === 1 ? emailMatches[0] : null;
  }

  const nameMatches = members.filter(
    (user) => partner && normalize(user.fullName) === partner
  );
  return nameMatches.length === 1 ? nameMatches[0] : null;
}

// Balance the entire batch, including saved/manual assignments, before suggesting new ones.
// Client continuity may justify at most one extra open review; larger load gaps win.
export function proposeMemberAssignments(
  links,
  users,
  context = [],
  current = {}
) {
  const metrics = Object.fromEntries(
    context.map((item) => [Number(item.userId), item])
  );
  const assigned = {};
  const reasons = {};
  const batchLoads = {};

  const add = (link, userId, reason) => {
    assigned[link.id] = userId;
    if (!link.hasGeneratedReview) {
      batchLoads[userId] = (batchLoads[userId] || 0) + 1;
    }
    if (reason) reasons[link.id] = reason;
  };

  for (const link of links) {
    const saved = link.assignments?.find(
      (item) => item.assignmentType === "PrimaryReviewer"
    )?.userId;
    const existing = current[link.id] || saved;
    if (existing) add(link, existing);
  }

  for (const link of [...links].sort((a, b) => Number(a.id) - Number(b.id))) {
    if (assigned[link.id] || link.hasGeneratedReview) continue;
    const engagement = link.engagement || link;
    const candidates = users.filter(
      (user) =>
        isEligibleInspectionMember(user) &&
        (!engagement.firmId ||
          Number(user.firmId) === Number(engagement.firmId))
    );
    if (!candidates.length) continue;

    const load = (user) =>
      Number(metrics[user.id]?.openReviews || 0) + (batchLoads[user.id] || 0);
    const minimumLoad = Math.min(...candidates.map(load));

    const experience = (user, field, id) =>
      id ? Number(metrics[user.id]?.[field]?.[id] || 0) : 0;
    const ranked = candidates
      .filter((user) => load(user) <= minimumLoad + 1)
      .sort(
        (a, b) =>
          experience(b, "clientExperience", engagement.clientId) -
            experience(a, "clientExperience", engagement.clientId) ||
          load(a) - load(b) ||
          Number(metrics[a.id]?.overdueReviews || 0) -
            Number(metrics[b.id]?.overdueReviews || 0) ||
          experience(b, "workTypeExperience", engagement.workTypeId) -
            experience(a, "workTypeExperience", engagement.workTypeId) ||
          experience(b, "industryExperience", engagement.industryId) -
            experience(a, "industryExperience", engagement.industryId) ||
          Number(a.id) - Number(b.id)
      );
    const member = ranked[0];
    const clientCount = experience(
      member,
      "clientExperience",
      engagement.clientId
    );
    add(
      link,
      member.id,
      `${load(
        member
      )} assigned reviews before this selection; ${clientCount} previous reviews for this client`
    );
  }

  return { assignments: assigned, reasons };
}
